// src/applicants/HiringRoomApplicantImportModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Pagination,
  Tooltip,
  Chip,
  Tabs,
  Tab,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  FileDownload as ImportIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import api from '../config/api';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';

const HiringRoomApplicantImportModal = ({ open, onClose, onImportComplete }) => {
  const { authToken, organization, project, showAlert } = useStore.getState();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [applicants, setApplicants] = useState([]);
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [currentTab, setCurrentTab] = useState(0);
  const limit = 20;

  // Filtros Tab 1: Por fechas
  const [dateFrom, setDateFrom] = useState(new Date(new Date().getFullYear(), 0, 1));
  const [dateTo, setDateTo] = useState(null);

  // Filtros Tab 2: Por datos
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [positions, setPositions] = useState([]);

  // Cargar positions al abrir el modal
  useEffect(() => {
    if (open) {
      loadPositions();
      // Cargar automáticamente con fecha desde por defecto si es Tab 1
      if (currentTab === 0) {
        handleSearch();
      }
    }
  }, [open]);

  // Recargar cuando cambie de tab
  useEffect(() => {
    if (open) {
      setApplicants([]);
      setSelectedApplicants([]);
      setCurrentPage(0);
      setTotal(0);

      // Si es Tab 1 (fechas) y hay fecha desde, cargar automáticamente
      if (currentTab === 0 && dateFrom) {
        handleSearch();
      }
    }
  }, [currentTab]);

  const loadPositions = async () => {
    try {
      const response = await api.get(
        '/positions',
        {
          params: { projectId: project },
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      if (response.data.response.status_response === 'OK') {
        // Solo positions importadas de HiringRoom
        const hrPositions = response.data.response.data.filter(
          pos => pos.hiringRoomVacancyId
        );
        setPositions(hrPositions);
      }
    } catch (err) {
      console.error('Error cargando positions:', err);
    }
  };

  const loadApplicants = async (page, filters = {}) => {
    try {
      setLoading(true);

      const params = {
        organizationId: organization,
        projectId: project,
        page: page,
        limit: limit
      };

      // Agregar filtros según estén definidos
      if (filters.createdFrom) params.createdFrom = filters.createdFrom;
      if (filters.createdTo) params.createdTo = filters.createdTo;
      if (filters.firstName) params.firstName = filters.firstName;
      if (filters.lastName) params.lastName = filters.lastName;
      if (filters.email) params.email = filters.email;
      if (filters.vacancyId) params.vacancyId = filters.vacancyId;

      const response = await api.get(
        '/applicants/hiringroom-applicants',
        {
          params,
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      if (response.data.response.status_response === 'OK') {
        const data = response.data.response.data;
        setApplicants(data.curriculums);
        setTotalPages(data.totalPaginas);
        setTotal(data.total);
      } else {
        showAlert(t('hiringRoomImport.loadError') + ': ' + response.data.response.message, 'error');
      }
    } catch (err) {
      console.error('Error cargando postulantes:', err);
      showAlert(t('hiringRoomImport.loadErrorDetail'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const filters = {};

    if (currentTab === 0) {
      // Tab 1: Búsqueda por fechas
      if (dateFrom) {
        filters.createdFrom = Math.floor(dateFrom.getTime() / 1000);
      }
      if (dateTo) {
        filters.createdTo = Math.floor(dateTo.getTime() / 1000);
      }
    } else {
      // Tab 2: Búsqueda por datos
      if (firstName.trim()) filters.firstName = firstName.trim();
      if (lastName.trim()) filters.lastName = lastName.trim();
      if (email.trim()) filters.email = email.trim();
      if (selectedPosition && selectedPosition.hiringRoomVacancyId) {
        filters.vacancyId = selectedPosition.hiringRoomVacancyId;
      }
    }

    setCurrentPage(0);
    setSelectedApplicants([]);
    loadApplicants(0, filters);
  };

  const handleSelectApplicant = (applicant, event) => {
    if (event) {
      event.stopPropagation();
    }

    if (applicant.alreadyImported) return;

    setSelectedApplicants(prev => {
      const isSelected = prev.some(a => a.id === applicant.id);
      if (isSelected) {
        return prev.filter(a => a.id !== applicant.id);
      } else {
        return [...prev, applicant];
      }
    });
  };

  const handleSelectAll = () => {
    const availableApplicants = applicants.filter(a => !a.alreadyImported);

    const allCurrentSelected = availableApplicants.every(a =>
      selectedApplicants.some(sa => sa.id === a.id)
    );

    if (allCurrentSelected) {
      setSelectedApplicants(prev =>
        prev.filter(sa => !availableApplicants.some(a => a.id === sa.id))
      );
    } else {
      const newSelections = availableApplicants.filter(
        a => !selectedApplicants.some(sa => sa.id === a.id)
      );
      setSelectedApplicants(prev => [...prev, ...newSelections]);
    }
  };

  const handleImport = async () => {
    if (selectedApplicants.length === 0) {
      showAlert(t('hiringRoomImport.selectAtLeastOne'), 'warning');
      return;
    }

    try {
      setImporting(true);

      const response = await api.post(
        '/applicants/import-hiringroom-applicants',
        { applicants: selectedApplicants },
        {
          params: {
            organizationId: organization,
            projectId: project
          },
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      if (response.data.response.status_response === 'OK') {
        const data = response.data.response.data;
        showAlert(
          t('hiringRoomImport.importSuccess', { count: data.totalImported }),
          'success'
        );

        if (data.totalErrors > 0) {
          console.warn('Errores en importación:', data.errors);
          showAlert(
            t('hiringRoomImport.importErrors', { count: data.totalErrors }),
            'warning'
          );
        }

        setSelectedApplicants([]);
        handleSearch(); // Recargar con los filtros actuales

        if (onImportComplete) {
          onImportComplete();
        }
      } else {
        showAlert(t('hiringRoomImport.importError') + ': ' + response.data.response.message, 'error');
      }
    } catch (err) {
      console.error('Error importando postulantes:', err);
      showAlert(t('hiringRoomImport.importErrorDetail'), 'error');
    } finally {
      setImporting(false);
    }
  };

  const handlePageChange = (event, value) => {
    const filters = {};

    if (currentTab === 0) {
      if (dateFrom) filters.createdFrom = Math.floor(dateFrom.getTime() / 1000);
      if (dateTo) filters.createdTo = Math.floor(dateTo.getTime() / 1000);
    } else {
      if (firstName.trim()) filters.firstName = firstName.trim();
      if (lastName.trim()) filters.lastName = lastName.trim();
      if (email.trim()) filters.email = email.trim();
      if (selectedPosition && selectedPosition.hiringRoomVacancyId) {
        filters.vacancyId = selectedPosition.hiringRoomVacancyId;
      }
    }

    setCurrentPage(value - 1);
    setSelectedApplicants([]);
    loadApplicants(value - 1, filters);
  };

  const handleClose = () => {
    setSelectedApplicants([]);
    setCurrentPage(0);
    setApplicants([]);
    setDateFrom(new Date(new Date().getFullYear(), 0, 1));
    setDateTo(null);
    setFirstName('');
    setLastName('');
    setEmail('');
    setSelectedPosition(null);
    setCurrentTab(0);
    onClose();
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const isApplicantSelected = (applicantId) => {
    return selectedApplicants.some(a => a.id === applicantId);
  };

  const availableApplicants = applicants.filter(a => !a.alreadyImported);
  const allCurrentSelected = availableApplicants.length > 0 &&
    availableApplicants.every(a => isApplicantSelected(a.id));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{t('hiringRoomImport.title')}</Typography>
          {total > 0 && (
            <Chip
              label={`${total} ${t('hiringRoomImport.applicantsFound')}`}
              color="primary"
              size="small"
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Tabs value={currentTab} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label={t('hiringRoomImport.searchByDates')} />
          <Tab label={t('hiringRoomImport.searchByData')} />
        </Tabs>

        {/* TAB 1: Búsqueda por Fechas */}
        {currentTab === 0 && (
          <Box sx={{ mb: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <DatePicker
                  label={t('hiringRoomImport.dateFrom')}
                  value={dateFrom ? dayjs(dateFrom) : null}
                  onChange={(newValue) => setDateFrom(newValue ? newValue.toDate() : null)}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  label={t('hiringRoomImport.dateTo')}
                  value={dateTo ? dayjs(dateTo) : null}
                  onChange={(newValue) => setDateTo(newValue ? newValue.toDate() : null)}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  {t('hiringRoomImport.search')}
                </Button>
              </Box>
            </LocalizationProvider>
          </Box>
        )}

        {/* TAB 2: Búsqueda por Datos */}
        {currentTab === 1 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                label={t('hiringRoomImport.firstName')}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
              />
              <TextField
                label={t('hiringRoomImport.lastName')}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
              />
              <TextField
                label={t('hiringRoomImport.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                size="small"
                sx={{ minWidth: 250 }}
              />
              <Autocomplete
                options={positions}
                getOptionLabel={(option) => option.name}
                value={selectedPosition}
                onChange={(event, newValue) => setSelectedPosition(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label={t('hiringRoomImport.position')} size="small" />
                )}
                sx={{ minWidth: 300 }}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
              >
                {t('hiringRoomImport.search')}
              </Button>
            </Box>
          </Box>
        )}

        {/* Resultados */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {applicants.length === 0 ? (
              <Alert severity="info">
                {total === 0 && currentPage === 0
                  ? t('hiringRoomImport.searchToSeeApplicants')
                  : t('hiringRoomImport.noApplicantsFound')}
              </Alert>
            ) : (
              <>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('hiringRoomImport.pageOfPages', { current: currentPage + 1, total: totalPages })}
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleSelectAll}
                    disabled={availableApplicants.length === 0}
                  >
                    {allCurrentSelected ? t('hiringRoomImport.deselectAll') : t('hiringRoomImport.selectAll')}
                  </Button>
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" width={50}></TableCell>
                        <TableCell><strong>{t('hiringRoomImport.firstName')}</strong></TableCell>
                        <TableCell><strong>{t('hiringRoomImport.email')}</strong></TableCell>
                        <TableCell><strong>{t('hiringRoomImport.phone')}</strong></TableCell>
                        <TableCell><strong>{t('hiringRoomImport.position')}</strong></TableCell>
                        <TableCell><strong>{t('hiringRoomImport.applicationDate')}</strong></TableCell>
                        <TableCell width={100}><strong>{t('hiringRoomImport.status')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {applicants.map((applicant) => (
                        <TableRow
                          key={applicant.id}
                          hover={!applicant.alreadyImported}
                          sx={{
                            cursor: applicant.alreadyImported ? 'not-allowed' : 'pointer',
                            backgroundColor: applicant.alreadyImported ? '#f5f5f5' : 'inherit',
                            opacity: applicant.alreadyImported ? 0.6 : 1
                          }}
                        >
                          <TableCell padding="checkbox">
                            {applicant.alreadyImported ? (
                              <Tooltip title={t('hiringRoomImport.alreadyImported')} arrow>
                                <BlockIcon color="disabled" fontSize="small" />
                              </Tooltip>
                            ) : (
                              <Checkbox
                                checked={isApplicantSelected(applicant.id)}
                                onChange={(e) => handleSelectApplicant(applicant, e)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                          </TableCell>
                          <TableCell onClick={() => !applicant.alreadyImported && handleSelectApplicant(applicant)}>
                            {`${applicant.nombre} ${applicant.apellido}`}
                          </TableCell>
                          <TableCell onClick={() => !applicant.alreadyImported && handleSelectApplicant(applicant)}>
                            {applicant.email}
                          </TableCell>
                          <TableCell onClick={() => !applicant.alreadyImported && handleSelectApplicant(applicant)}>
                            {applicant.telefonoCelular || t('hiringRoomImport.notAvailable')}
                          </TableCell>
                          <TableCell onClick={() => !applicant.alreadyImported && handleSelectApplicant(applicant)}>
                            {applicant.vacanteNombre || t('hiringRoomImport.notAvailable')}
                          </TableCell>
                          <TableCell onClick={() => !applicant.alreadyImported && handleSelectApplicant(applicant)}>
                            {applicant.fechaPostulacion || t('hiringRoomImport.notAvailable')}
                          </TableCell>
                          <TableCell>
                            {applicant.alreadyImported ? (
                              <Chip
                                label={t('hiringRoomImport.imported')}
                                size="small"
                                color="success"
                                icon={<CheckCircleIcon />}
                              />
                            ) : (
                              <Chip
                                label={applicant.etapa || t('hiringRoomImport.new')}
                                size="small"
                                color="default"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage + 1}
                      onChange={handlePageChange}
                      color="primary"
                    />
                  </Box>
                )}

                {selectedApplicants.length > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {t('hiringRoomImport.applicantsSelected', { count: selectedApplicants.length })}
                  </Alert>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>
          {t('hiringRoomImport.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={loading || importing || selectedApplicants.length === 0}
          startIcon={importing ? <CircularProgress size={20} /> : <ImportIcon />}
        >
          {importing
            ? t('hiringRoomImport.importing')
            : t('hiringRoomImport.importSelected', { count: selectedApplicants.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HiringRoomApplicantImportModal;